export default class EventFeedCtrl {
  constructor(
    $scope,
    $timeout,
    $filter,
    AvatarUrl,
    BenchUrl,
    Client,
    ClientSettings,
    LocalStorageStore,
    MessageCollection,
    MessageParser,
    urlParser,
    User,
    UnreadMessages,
    featureFlagService,
  ) {
    this.$scope = $scope;
    this.$timeout = $timeout;
    this.$filter = $filter;
    this.avatarUrl = AvatarUrl.getAvatar;
    this.BenchUrl = BenchUrl;
    this.Client = Client;
    this.ClientSettings = ClientSettings;
    this.LocalStorageStore = LocalStorageStore;
    this.MessageCollection = MessageCollection;
    this.messageParser = MessageParser;
    this.user = User;
    this.urlParser = urlParser;
    this.UnreadMessages = UnreadMessages;

    this.firstUnreadMessage = {};
    this.isNewMessageIndicatorFaded = false;
    this.actionsVisible = false;
    this.requestsViewExpanded = false;
    this.messageInputFocus = true;
    this.newMessageIndicatorInView = true;
    this.markedAllAsRead = false;
    this.messageNoticeVisible = false;

    this.featureFlagService = featureFlagService;
  }

  $onInit() {
    this.viewerIsBK = this.user.isBookkeeper();
    this.bookkeeperId = this.user.isBookkeeper() ? this.userId : 0;
    this.client = new this.Client(this.clientId);
    this.userGroup = this.user.userGroup();
    this.personCollection = this.client.$assignedPersons();
    this.settings = new this.ClientSettings(this.user.getClientId());

    this.messages = new this.MessageCollection({
      viewerId: this.userId,
      clientId: this.clientId,
      full: true,
      roles: this.user.roles,
      inboxState: this.client.inboxState,
    });

    this.message = new this.LocalStorageStore(this.messages.$storeId());
    this.unreadMessages = new this.UnreadMessages(this.clientId, {
      viewerId: this.userId,
      viewerUserGroup: this.userGroup,
    });

    this.messages.$onSet(() => this.onMessageCollectionSet());
    this.unreadMessages.$onSet(() => this.onUnreadMessagesSet());

    this.$scope.$watch('vm.message.text', text => this.message.$save({ text }));

    // Scroll to the bottom after page renders
    this.$timeout(() => this.scrollToBottom());
  }

  onMessageCollectionSet() {
    this.isNewMessageIndicatorFaded = false;
    this.markedAllAsRead = false;

    if (this.messages.$findFirstUnviewed() !== {}) {
      this.firstUnreadMessage = this.messages.$findFirstUnviewed();
    }

    if (this.unreadMessages.$lastUpdated > 0) {
      this.fetchOutstandingUnreadMessages();
    }
  }

  onUnreadMessagesSet() {
    if (this.messages.$lastUpdated > 0) {
      this.fetchOutstandingUnreadMessages();
    }
  }

  showMessageFormatting() {
    return this.featureFlagService.isMessageFormattingEnabled();
  }

  onLoadMore() {
    return this.messages.$fetchMore();
  }

  fetchOutstandingUnreadMessages() {
    const loadedUnreadCount = this.messages.unviewedLoadedMessageCountBeforeTimestamp(
      this.unreadMessages.newestUnreadDate,
    );
    const outstandingUnreadMessageCount = this.unreadMessages.count - loadedUnreadCount;

    if (outstandingUnreadMessageCount <= 0 || !this.messages.$resolved) return;

    const before = this.messages.getEarliestMessageDate();
    const batchSize = this.messages.limit;

    // This is rounded to the nearest multiple of the default limit so that it
    // plays nice with the isHistoryComplete method on the message collection
    const limit = Math.ceil(outstandingUnreadMessageCount / batchSize) * batchSize;

    this.messages.$fetch({}, { before, limit });
  }

  fadeNewMessageIndicator() {
    this.$timeout(() => (this.isNewMessageIndicatorFaded = true), 3000);
  }

  clientView() {
    return this.user.isImpersonatingClient() || this.user.isClient() || this.user.isTA();
  }

  isMessageRight(message) {
    return this.clientView() === message.$isAuthorClient();
  }

  isMessageByOther(message) {
    return this.isMessageRight(message) && !message.$isAuthor(this.userId);
  }

  toggleRequestsView() {
    return (this.requestsViewExpanded = !this.requestsViewExpanded);
  }

  postMessage(sms) {
    if (!this.isMessageValid(this.message.text)) {
      return false;
    }

    const parsers = this.user.isBookkeeper() ? [this.urlParser] : [];

    const { message, assets } = this.messageParser.parse(this.message.text, parsers);
    if (!message) {
      return;
    }

    // Pass sms object to the $post method
    this.messages.$post(message, assets, sms);

    this.message.$set({ text: '' });

    this.scrollToBottom();

    return true;
  }

  isMessageValid(message) {
    if (this.user.isBookkeeper() && !this.BenchUrl.validBenchLinks(message)) {
      return false;
    }

    return message !== '';
  }

  hasValidBenchLinks(message) {
    return this.BenchUrl.validBenchLinks(message);
  }

  isFirstUnreadMessage(message) {
    return this.firstUnreadMessage.id === message.id;
  }

  isNewDate(dateA, dateB) {
    return new Date(dateA).toDateString() !== new Date(dateB).toDateString();
  }

  isNewAuthor(messageA, messageB) {
    return messageA.context.eventSource.authorId !== messageB.context.eventSource.authorId;
  }

  showAvatar(messageA, messageB) {
    return messageA != null && messageB != null && this.isNewAuthor(messageA, messageB);
  }

  showNewTime(messageA, messageB) {
    const maxDelay = 15 * 60 * 1000;
    const isNewAuthor = this.isNewAuthor(messageA, messageB);
    const isLongDelay = Math.abs(messageB.createdAt - messageA.createdAt) > maxDelay;
    const componentA = messageA.context.eventSource.component;
    const componentB = messageB.context.eventSource.component;
    const isFromNewSource = componentA !== componentB;

    return isNewAuthor || isLongDelay || isFromNewSource;
  }

  markAllViewed() {
    if (this.viewerIsBK) return;

    this.messages.$markAllViewed();
    this.unreadMessages.clearCount();
  }

  showMessageNotice() {
    return (this.messageNoticeVisible = true);
  }

  currentTime() {
    return Date.now();
  }

  scrollToBottom() {
    if (typeof this.$scope.$scrollToBottom !== 'function') return;

    // HACK: abuses scope sharing to call $scrollToBottom on blMaintainScrollBottom!
    this.$scope.$scrollToBottom();
  }

  hideActions() {
    return (this.actionsVisible = false);
  }

  getMessageRecipientDescription() {
    if (this.user.isClient() || this.user.isImpersonatingClient()) {
      return 'Your Bookkeeping Team';
    }
    return this.client.fullName;
  }

  onNewMessageIndicatorInView() {
    this.newMessageIndicatorInView = true;
    this.markAllViewed();
    this.fadeNewMessageIndicator();
  }

  onNewMessageIndicatorOutOfView() {
    this.newMessageIndicatorInView = false;
  }

  get unreadMessageCount() {
    return this.unreadMessages.count;
  }

  get unreadMessageDate() {
    return this.$filter('date')(this.unreadMessages.newestUnreadTimestamp, 'MMMM d');
  }

  get showUnreadMessageBar() {
    if (this.viewerIsBK) return false;

    return (
      this.unreadMessages.count > 0 && !this.newMessageIndicatorInView && !this.markedAllAsRead
    );
  }

  jumpToNewMessages() {
    const newMessages = document.querySelector('#new-messages');
    newMessages.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }

  markAllMessagesRead() {
    this.markedAllAsRead = true;
    this.markAllViewed();
  }

  get moreMessages() {
    return !this.messages.isHistoryComplete();
  }
}
